import React from "react";
import { Route, Navigate } from "react-router-dom";
import MainLayout from "../../components/MainLayout/MainLayout";
import { AuthenticationGuard } from "@/components/AuthenticationGuard/AuthenticationGuard";
import PermissionGuard from "../../components/PermissionGuard/PermissionGuard";
import HubDashboard from "../../modules/hub/features/HubDashboard/HubDashboard";
import UserList from "../../modules/hub/features/UserManagement/components/UserList/UserList";
import EditUser from "../../modules/hub/features/UserManagement/components/EditUser/EditUser";
import CreateUser from "../../modules/hub/features/UserManagement/components/CreateUser/CreateUser";
import SettingsLayout from "../../modules/hub/features/UserAppSettings/components/SettingsLayout/SettingsLayout";
import UserProfile from "../../modules/hub/features/UserAppSettings/components/UserProfile/UserProfile";
import CompanyProfile from "../../modules/hub/features/UserAppSettings/components/CompanyProfile/CompanyProfile";
import SubscriptionSettings from "../../modules/hub/features/UserAppSettings/components/SubscriptionSettings/SubscriptionSettings";
import AnnualRevenue from "../../modules/hub/features/UserAppSettings/components/AnnualRevenue/AnnualRevenue";
import Integrations from "../../modules/hub/features/UserAppSettings/components/Integrations/Integrations";
import BillingSubscriptionsPage from "@/modules/hub/features/Billing/components/BillingSubscriptionsPage/BillingSubscriptionsPage";
import BillingSubscriptionInvoicesPage from "@/modules/hub/features/Billing/components/BillingSubscriptionInvoicesPage/BillingSubscriptionInvoicesPage";
import BillingInvoicesPage from "@/modules/hub/features/Billing/components/BillingInvoicesPage/BillingInvoicesPage";
import CoachingDashboard from "@/modules/hub/features/CoachingDashboard/components/CoachingDasboard/CoachingDashboard";
import { ResourcesLibraryV2 as ResourceLibraryV2 } from "@/modules/hub/features/ResourceLibraryV2/components/ResourceLibraryV2/ResourceLibraryV2";
import ResourceDetailsV2 from "@/modules/hub/features/ResourceLibraryV2/components/ResourceDetails/ResourceDetails";
import {
  ResourceLibraryGate,
  ResourceDetailsGate,
} from "@/modules/hub/features/ResourceLibraryV2/components/ResourceLibraryGate/ResourceLibraryGate";
import { Podcasts } from "@/modules/hub/features/Podcasts/components/Podcasts/Podcasts";
import { PodcastsV2 } from "@/modules/hub/features/PodcastsV2/components/PodcastsV2/PodcastsV2";
import { PartnerNetwork } from "@/modules/hub/features/PartnerNetwork/components/PartnerNetwork/PartnerNetwork";
import { PartnerNetworkV2 } from "@/modules/hub/features/PartnerNetworkV2/components/PartnerNetworkV2/PartnerNetworkV2";
import { News } from "@/modules/hub/features/News/components/News/News";
import { NewsV2 } from "@/modules/hub/features/NewsV2/components/NewsV2/NewsV2";
import StageFeatureMatrix from "@/modules/hub/features/StageFeatureMatrix/components/StageFeatureMatrix/StageFeatureMatrix";
import permissionsService from "@/services/permissionsService";
import { getUnifiedNavigation } from "@/navigation/unifiedNavigation";
import {
  filterNavigationByAccess,
  firstReachablePage,
} from "@/utils/navigationHelpers";
import { useAppSelector } from "@/app/hooks";
import PreEpStepsCard from "@/modules/hub/features/PreEpWelcome/components/PreEpStepsCard/PreEpStepsCard";
import { isPreEpCompany } from "@/modules/hub/features/PreEpWelcome/config/preEpStage";
// Create stable component reference
const HubMainLayout = () => <MainLayout section="hub" />;

// Hub home: users with the "resources" permission see the dashboard; everyone
// else goes to the first sidebar page they can open (403 if none). A Pre-EP
// company's own users never get here (PreEpGate sends them to /pre-ep);
// CertainPath staff working in one see the dashboard with a card that opens
// the members' steps.
const HubHome = () => {
  const { hasPermission, hasCertainPathCompany, hasCertainPathEmployee } =
    permissionsService();
  const isPreEp = useAppSelector((state) =>
    isPreEpCompany(state.userAppSettings.userAppSettings),
  );

  if (isPreEp) {
    return <HubDashboard banner={<PreEpStepsCard />} />;
  }

  return hasPermission("resources") ? (
    <HubDashboard />
  ) : (
    <Navigate
      replace
      to={
        firstReachablePage(
          filterNavigationByAccess(
            getUnifiedNavigation(),
            hasPermission,
            hasCertainPathCompany(),
            hasCertainPathEmployee(),
          ),
          "/hub",
        ) ?? "/403"
      }
    />
  );
};

/**
 * HubRoutes
 *
 * Wraps the /hub section in the Auth0-based authentication guard, then
 * protects individual routes with PermissionGuard based on the required
 * permissions from your navigation config.
 */
const HubRoutes = (
  <Route
    element={<AuthenticationGuard component={HubMainLayout} />}
    path="/hub"
  >
    <Route element={<HubHome />} index />

    <Route element={<CoachingDashboard />} path="dashboards/coaching" />

    {/* User Management routes - accessible via user-management permission */}
    <Route
      element={
        <PermissionGuard requiredPermissions={["user-management"]}>
          <UserList />
        </PermissionGuard>
      }
      path="users"
    />
    <Route
      element={
        <PermissionGuard requiredPermissions={["user-management"]}>
          <CreateUser />
        </PermissionGuard>
      }
      path="users/create"
    />
    <Route
      element={
        <PermissionGuard requiredPermissions={["user-management"]}>
          <EditUser />
        </PermissionGuard>
      }
      path="users/:uuid/edit"
    />

    {/* Settings - nested routes with side navigation */}
    <Route element={<SettingsLayout />} path="settings">
      <Route element={<Navigate replace to="profile" />} index />
      <Route element={<UserProfile />} path="profile" />
      <Route
        element={
          <PermissionGuard requiredPermissions={["company-profile"]}>
            <CompanyProfile />
          </PermissionGuard>
        }
        path="company"
      />
      <Route
        element={
          <PermissionGuard requiredPermissions={["company-profile"]}>
            <SubscriptionSettings />
          </PermissionGuard>
        }
        path="subscription"
      />
      <Route
        element={
          <PermissionGuard requiredPermissions={["integrations"]}>
            <Integrations />
          </PermissionGuard>
        }
        path="integrations"
      />
      <Route
        element={
          <PermissionGuard requiredPermissions={["integrations"]}>
            <Integrations />
          </PermissionGuard>
        }
        path="integrations/servicetitan"
      />
      <Route
        element={
          <PermissionGuard requiredPermissions={["integrations"]}>
            <Integrations />
          </PermissionGuard>
        }
        path="integrations/quickbooks"
      />
      <Route
        element={
          <PermissionGuard requiredPermissions={["annual-revenue"]}>
            <AnnualRevenue />
          </PermissionGuard>
        }
        path="annual-revenue"
      />
    </Route>

    <Route
      element={
        <PermissionGuard requiredPermissions={["billing"]}>
          <BillingSubscriptionsPage />
        </PermissionGuard>
      }
      path="billing/subscriptions"
    />
    <Route
      element={
        <PermissionGuard requiredPermissions={["billing"]}>
          <BillingSubscriptionInvoicesPage />
        </PermissionGuard>
      }
      path="billing/subscriptions/:id/invoices"
    />
    <Route
      element={
        <PermissionGuard requiredPermissions={["billing"]}>
          <BillingInvoicesPage />
        </PermissionGuard>
      }
      path="billing/invoices"
    />

    {/* Stage members get V2, everyone else legacy — same URLs for both. */}
    <Route
      element={
        <PermissionGuard requiredPermissions={["resources"]}>
          <ResourceLibraryGate />
        </PermissionGuard>
      }
      path="resources"
    />
    {/* V2's internal links use these paths; also the QA/preview URL. */}
    <Route
      element={
        <PermissionGuard requiredPermissions={["resources"]}>
          <ResourceLibraryV2 />
        </PermissionGuard>
      }
      path="resources-v2"
    />
    <Route
      element={
        <PermissionGuard requiredPermissions={["resources"]}>
          <ResourceDetailsV2 />
        </PermissionGuard>
      }
      path="resources-v2/:slug"
    />
    <Route
      element={
        <PermissionGuard requiredPermissions={["resources"]}>
          <ResourceDetailsGate />
        </PermissionGuard>
      }
      path="resources/:slug"
    />

    <Route
      element={
        <PermissionGuard requiredPermissions={["resources"]}>
          <News />
        </PermissionGuard>
      }
      path="news"
    />
    <Route
      element={
        <PermissionGuard requiredPermissions={["resources"]}>
          <NewsV2 />
        </PermissionGuard>
      }
      path="news-v2"
    />
    <Route
      element={
        <PermissionGuard requiredPermissions={["resources"]}>
          <Podcasts />
        </PermissionGuard>
      }
      path="podcasts"
    />
    <Route
      element={
        <PermissionGuard requiredPermissions={["resources"]}>
          <PodcastsV2 />
        </PermissionGuard>
      }
      path="podcasts-v2"
    />
    <Route
      element={
        <PermissionGuard
          requiredPermissions={["partner-network", "partner-network-admin"]}
        >
          <PartnerNetwork />
        </PermissionGuard>
      }
      path="partner-network"
    />
    <Route
      element={
        <PermissionGuard
          requiredPermissions={["partner-network", "partner-network-admin"]}
        >
          <PartnerNetworkV2 />
        </PermissionGuard>
      }
      path="partner-network-v2"
    />

    <Route element={<StageFeatureMatrix />} path="growth-stages" />
  </Route>
);

export default HubRoutes;
